// #genai GuestAccess Redux reducer.
// Single source of truth for guest/shared-resource flow (includes former SharedStartup state).
import {
  SET_SHARED_CONTEXT,
  SET_SHARED_ACCESS,
  SET_SHARED_PHASE,
  RESET_SHARED,
} from 'features/shared-startup/SharedStartup.actions';
import {
  SET_GUEST_RESOURCE,
  SET_GUEST_USER_INFO,
  SET_GUEST_VERIFICATION,
  SET_GUEST_VERIFIED,
  SET_GUEST_FLOW_PHASE,
  SET_GUEST_ERROR,
  RESET_GUEST,
  SET_GUEST_HAS_ACCESS,
} from './GuestAccess.actions';
import { ACCESS_PHASES } from '../../constants/GuestAccess';

/**
 * Shape of the GuestAccess slice.
 *
 * Resource validation (validateResourcePath & requestPreAccessToken)
 * ──────────────────────────────────────────────────────────────────
 * isResourceShared   – true when BOTH validateResourcePath (valid:true) AND requestPreAccessToken (isResourceShared:true)
 * isPreAccessTokenOk – requestPreAccessToken: pre-access token was successfully issued
 * preAccessToken     – requestPreAccessToken: stored under ev_pre_access_token in localStorage (NOT in 'token')
 * resourceShareId    – requestPreAccessToken: UUID identifying the share record
 * resourceId         – requestPreAccessToken: backend resource identifier
 * appCode            – requestPreAccessToken / URL: application code
 * resourcePath       – full resource path parsed from the URL
 *
 * User identity (checkIsEvUser)
 * ─────────────────────────────
 * isEvUser   – checkIsEvUser: email belongs to an existing EV account
 * loginId    – checkIsEvUser: identifier passed to sendEmailVerification
 * email      – email address entered by the user in the sign-in form
 *
 * Email verification (sendEmailVerification)
 * ──────────────────────────────────────────
 * verificationId – sendEmailVerification: ID used for polling (pollEmailVerification)
 * emailSent      – sendEmailVerification: guest sign-in email was dispatched successfully
 *
 * Polling result (pollEmailVerification)
 * ─────────────────────────────────────
 * isVerified      – pollEmailVerification: user clicked the link and the backend confirmed
 * customAuthToken – pollEmailVerification: auth token returned after successful verification
 *
 * Meta
 * ────
 * guestFlowPhase – current phase of the overall flow (see action comment for values)
 * error          – last error message (null when clean)
 *
 * Gate / direct-mount (formerly SharedStartup) #genai
 * sharedPhase, appUrl, appName – gate phase and getAppDetails result for guest direct mount
 */
const initialState = {
  isResourceShared: false,
  isPreAccessTokenOk: false,
  preAccessToken: '', // informational only — actual token lives in ev_pre_access_token
  resourceShareId: '',
  resourceId: '',
  appCode: '',
  resourcePath: '',

  isEvUser: false,
  loginId: '',
  email: '',

  verificationId: '',
  emailSent: false,

  isVerified: false,
  customAuthToken: '',

  guestFlowPhase: ACCESS_PHASES.IDLE,
  error: null,

  hasAccess: null, // null = pending, true = access granted, false = denied. #genai
  resourceAccessAuditId: null, // ID from checkResourceAccess response, used for backend status updates. #genai

  sharedPhase: ACCESS_PHASES.LOADING,
  appUrl: '',
  appName: '',
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_GUEST_RESOURCE:
    case SET_GUEST_USER_INFO:
    case SET_GUEST_VERIFICATION:
    case SET_GUEST_VERIFIED:
      return { ...state, ...action.payload };

    case SET_GUEST_FLOW_PHASE:
      return { ...state, guestFlowPhase: action.payload };

    case SET_GUEST_ERROR:
      return { ...state, error: action.payload };

    case SET_GUEST_HAS_ACCESS:
      return {
        ...state,
        hasAccess: action.payload?.hasAccess ?? action.payload,
        resourceAccessAuditId: action.payload?.resourceAccessAuditId ?? null,
      };

    case RESET_GUEST:
      return { ...initialState };

    // Former SharedStartup actions — keep one common state. #genai
    case SET_SHARED_CONTEXT: {
      const p = action.payload || {};
      return {
        ...state,
        ...(p.shared !== undefined && { isResourceShared: p.shared }),
        ...(p.appCode !== undefined && { appCode: p.appCode }),
        ...(p.resourcePath !== undefined && { resourcePath: p.resourcePath }),
        ...(p.resourceShareId !== undefined && { resourceShareId: p.resourceShareId }),
        ...(p.appUrl !== undefined && { appUrl: p.appUrl }),
        ...(p.appName !== undefined && { appName: p.appName }),
      };
    }
    case SET_SHARED_ACCESS: {
      const p = action.payload || {};
      return {
        ...state,
        ...(p.isEVUser !== undefined && { isEvUser: p.isEVUser }),
        ...(p.hasAccess !== undefined && { hasAccess: p.hasAccess }),
        ...(p.resourceAccessAuditId !== undefined && { resourceAccessAuditId: p.resourceAccessAuditId }),
      };
    }
    case SET_SHARED_PHASE:
      return { ...state, sharedPhase: action.payload };
    case RESET_SHARED:
      return {
        ...state,
        sharedPhase: ACCESS_PHASES.LOADING,
        appUrl: '',
        appName: '',
        isResourceShared: false,
        appCode: '',
        resourcePath: '',
        resourceShareId: '',
        isEvUser: false,
        hasAccess: false,
        resourceAccessAuditId: null,
      };

    default:
      return state;
  }
};

export default reducer;
